import {Component, EventEmitter, forwardRef, Input, OnDestroy, Output} from '@angular/core';
import {AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators} from '@angular/forms';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {concatMap, map, tap} from 'rxjs/operators';
import {MergeQuestiondataService} from '../../services/merge-questiondata.service';
import {QuestionItemListService} from './question-item-list.service';
import {IIdentifier} from 'build/project/openapi-cast/project/project-openapi-cast.model';
import {IAllFieldItems, IAllQuestion} from '../field-item-row/row.model';
import {IErrorCodes, IFunction, IMetaContent, ITEMTYPE} from 'src/app/shared/components/field-item/field-item.model';

@Component({
   selector: 'app-question-item-list',
   templateUrl: './question-item-list.component.html',
   styleUrls: ['./question-item-list.component.scss'],
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         multi: true,
         useExisting: forwardRef(() => QuestionItemListComponent)
      },
      {
         provide: NG_VALIDATORS,
         multi: true,
         useExisting: QuestionItemListComponent
      },
      QuestionItemListService
   ],
   standalone: false
})
export class QuestionItemListComponent implements ControlValueAccessor, Validators, OnDestroy {

   private id_: IIdentifier = {} as IIdentifier;
   private questionItemMetaArray_: IAllFieldItems[] = [];
   private questionMetaContentArray_: IMetaContent[] = [];
   private done_: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
   public done$: Observable<boolean> = this.done_.asObservable();
   public itemListFormGroup: FormGroup = this.fb.group({});
   private error_: IErrorCodes = {} as IErrorCodes;

   @Input()
   set id (val: IIdentifier) {
      this.reset();
      if (!val) return;
      this.sMergeQuestiondata.loadData(val).pipe(
         map(questionArray => questionArray && questionArray.length > 0 ? this.sQuestionItem.sortArray(questionArray) : this.setNoData()),
         map((questionArraySorted: boolean | IAllQuestion[])  => {
            if (typeof questionArraySorted == "object") {
               this.questionItemMetaArray_ = this.sQuestionItem.modifyArray(questionArraySorted, val);
               this.questionMetaContentArray_ = this.questionItemMetaArray as IMetaContent[];
            }
            return questionArraySorted;
         }),
         concatMap(_ => this.initializeFormGroup()),
         tap(_ => this.done_.next(true))
      ).subscribe(_ => {
         this.setDependencies(false, null);
      });
      this.id_ = val;
   }

   get id (): IIdentifier {
      return this.id_;
   }


   @Input()
   set value (val: IMetaContent[]) {
      this.questionMetaContentArray_ = val;
   }

   get value (): IMetaContent[] {
      return this.questionMetaContentArray;
   }

   @Output() public noContent: EventEmitter<boolean> = new EventEmitter<boolean>();

   get error (): IErrorCodes {
      return this.error_;
   }

   get questionItemMetaArray (): IAllFieldItems[] {
      return this.questionItemMetaArray_;
   }

   get questionMetaContentArray (): IMetaContent[] {
      return this.questionMetaContentArray_;
   }

   constructor (private fb: FormBuilder, public sQuestionItem: QuestionItemListService, private sMergeQuestiondata: MergeQuestiondataService) { }

   ngOnDestroy (): void {
      this.reset();
   }

   private setNoData (): boolean {
      this.noContent.emit(true);
      return false;
   }

   private initializeFormGroup (): Observable<boolean> {
      if (this.questionItemMetaArray.length > 0) {
         this.questionItemMetaArray.map(value => {
            this.itemListFormGroup.addControl(`questionItem_${value.question_id}`, this.fb.control(''));
            this.itemListFormGroup.get(`questionItem_${value.question_id}`)?.valueChanges.subscribe(content => {
               this.questionMetaContentArray_ = this.sQuestionItem.createIQuestionMetaContent(this.questionMetaContentArray, value.question_id, content);
               this.setDependencies(true, value.question_id);
               this.onChange(this.value);
               this.onTouched();
            });
            this.setDependencies(true, value.question_id);
         });
      }
      return of(true);
   }

   private setDependencies (isChanged: boolean, question_id: string | null): void {
      if(question_id == null) return;
      this.questionItemMetaArray_.forEach((value: IAllQuestion) => {
         if (!isChanged || value.dependsQuestion == question_id) {
            switch (value.dependsType) {
               case "show":
                  this.setDependenciesShow(value);
                  break;
               case "valid":
                  this.setDependenciesValid(value);
                  break;
               case "content":
                  this.setDependenciesContent(value);
                  break;
               default:
                  break;
            }
         }
      });
   }

   private setDependenciesShow (value: IAllQuestion): void {
      if (this.itemListFormGroup.get(`questionItem_${value.dependsQuestion}`)?.disabled) {
         this.itemListFormGroup.get(`questionItem_${value.question_id}`)?.disable();
         this.questionItemMetaArray[this.questionItemMetaArray.findIndex(x => x.question_id === value.question_id)].disabled = true;
      } else {
         this.itemListFormGroup.get(`questionItem_${value.question_id}`)?.enable();
         this.questionItemMetaArray[this.questionItemMetaArray.findIndex(x => x.question_id === value.question_id)].disabled = false;
      }
   }

   private setDependenciesValid (value: IAllQuestion): void {
      if (this.itemListFormGroup.get(`questionItem_${value.dependsQuestion}`)?.valid && this.itemListFormGroup.get(`questionItem_${value.dependsQuestion}`)?.dirty) {
         this.itemListFormGroup.get(`questionItem_${value.question_id}`)?.enable();
         this.questionItemMetaArray[this.questionItemMetaArray.findIndex(x => x.question_id === value.question_id)].disabled = false;
      } else {
         this.itemListFormGroup.get(`questionItem_${value.question_id}`)?.disable();
         this.questionItemMetaArray[this.questionItemMetaArray.findIndex(x => x.question_id === value.question_id)].disabled = true;
      }
   }

   private setDependenciesContent (value: IAllQuestion): void {
      let isOK: boolean = false;
      if (!this.itemListFormGroup.get(`questionItem_${value.dependsQuestion}`)?.disabled &&
      this.itemListFormGroup.get(`questionItem_${value.dependsQuestion}`)?.value.content) {
         const array: string[] | undefined = value.dependsOption?.split(";");
         array?.filter(content => this.itemListFormGroup.get(`questionItem_${value.dependsQuestion}`)?.value.content == content).forEach(_ => isOK = true);
      }

      if (isOK) {
         this.itemListFormGroup.get(`questionItem_${value.question_id}`)?.enable();
         this.questionItemMetaArray[this.questionItemMetaArray.findIndex(x => x.question_id == value.question_id)].disabled = false;
      } else {
         this.itemListFormGroup.get(`questionItem_${value.question_id}`)?.disable();
         this.questionItemMetaArray[this.questionItemMetaArray.findIndex(x => x.question_id == value.question_id)].disabled = true;
      }
   }

   writeValue (_selected: string): void {
   }

   registerOnChange (fn: any): void {
      this.onChange = fn;
   }

   registerOnTouched (fn: any): void {
      this.onTouched = fn;
   }
   
   validate (_control: AbstractControl): ValidationErrors | null {
      this.itemListFormGroup.updateValueAndValidity({emitEvent: true, onlySelf: true});
      return this.itemListFormGroup.invalid ? {invalid: true} : null;
   }

   private onTouched () {}

   private onChange (_: IMetaContent[]) {}
   
   setDisabledState (_isDisabled: boolean | undefined): void {
   }

   private reset (): void {
      this.itemListFormGroup = this.fb.group({});

      this.questionItemMetaArray_ = [];
      this.questionMetaContentArray_ = [];
      this.error_ = {} as IErrorCodes;
   
   }

   public setValueFunction (value: IFunction) {
      this.questionItemMetaArray_.filter(val => val.fieldtype!.toLowerCase() == ITEMTYPE.toggle).forEach(item => this.itemListFormGroup.get("questionItem_" + item.question_id)?.setValue(value.value, {emitEvent: false}));
   }

}
